import type { AoSGameState, IssueSharesArgs } from '../../types';
import type { Ctx } from 'boardgame.io';

export interface ValidationResult {
  success: boolean;
  error?: string;
  message?: string;
}

/**
 * 株式発行のバリデーション（全マップ共通）
 *
 * config で制御:
 * - maxSharesLimit: 総発行上限（デフォルト: 15、Detroit: 25）
 * - maxSharesPerTurn: 1ターン発行上限（Barbados: 1、他: undefined = 無制限）
 *
 * @param G - ゲーム状態
 * @param ctx - Boardgame.ioコンテキスト
 * @param args - 株式発行引数
 * @returns バリデーション結果
 */
export function validateIssueShares(
  G: AoSGameState,
  ctx: Ctx,
  args: IssueSharesArgs
): ValidationResult {
  const player = G.players[ctx.currentPlayer];
  const maxSharesLimit = G.mapConfig?.maxSharesLimit ?? 15;
  const maxSharesPerTurn = G.mapConfig?.maxSharesPerTurn;

  // 発行数チェック: 0以上
  if (args.issueCount < 0) {
    return {
      success: false,
      error: 'INVALID_ISSUE_COUNT',
      message: 'Issue count must be >= 0'
    };
  }

  // ターン発行上限チェック（maxSharesPerTurn が設定されている場合）
  if (maxSharesPerTurn !== undefined && maxSharesPerTurn > 0) {
    // 1回の発行で上限を超えないかチェック
    if (args.issueCount > maxSharesPerTurn) {
      return {
        success: false,
        error: 'EXCEEDS_PER_TURN_LIMIT',
        message: `Issue count ${args.issueCount} exceeds per-turn limit of ${maxSharesPerTurn}`
      };
    }

    // 今ターン既に発行済みの場合
    const currentTurnSharesIssued = player.currentTurnSharesIssued ?? 0;
    if (currentTurnSharesIssued + args.issueCount > maxSharesPerTurn) {
      return {
        success: false,
        error: 'MAX_SHARES_PER_TURN_EXCEEDED',
        message: `Already issued ${currentTurnSharesIssued} share(s) this turn (max: ${maxSharesPerTurn})`
      };
    }
  }

  // 最大株式数チェック（総発行上限）
  const newShares = player.sharesIssued + args.issueCount;
  if (newShares > maxSharesLimit) {
    return {
      success: false,
      error: 'MAX_SHARES_EXCEEDED',
      message: `Maximum shares exceeded: ${newShares} (max: ${maxSharesLimit})`
    };
  }

  return { success: true };
}
